import type { AppListenerApi } from "@src/store/rootState";
import { actions } from "@src/store/slices/experienceBuilder/common/actions";
import { getEmbeddedState } from "@src/store/slices/experienceBuilder/common/selectors/getEmbeddedState";
import { isPageLoading } from "@src/store/slices/experienceBuilder/common/selectors/isPageLoading";

import { NAVIGATION_END_TIMEOUT, NAVIGATION_START_TIMEOUT } from "../constants";

// Wait for an iframe navigation to start (briefly) and then settle. Shared by
// the navigate handler (direct + picker branches) and the preview-state settle
// below.
export const waitForPageSettle = async (api: AppListenerApi) => {
  const started = await Promise.race([
    api.take((_, state) => isPageLoading(state)),
    api.delay(NAVIGATION_START_TIMEOUT),
  ]);
  if (!started) return;
  await Promise.race([
    api.take((_, state) => !isPageLoading(state)),
    api.delay(NAVIGATION_END_TIMEOUT),
  ]);
};

// Run a preview-mutating effect (update_mods / set_variation / set_device), then
// settle — but ONLY if it actually changed the embedded preview state. That
// change is exactly what makes `setupSendPreviewStateListener` post
// `ig-previewState`, to which the plugin responds by reloading (→ page-load) or
// processing it live (→ `ig-setPreviewStateProcessed`, bridged to
// `previewStateProcessed`); race both. The `getEmbeddedState` reference check
// mirrors the send listener's own predicate exactly, so "we wait" ⟺ "an
// `ig-previewState` was sent"; when the effect leaves the embedded state
// untouched (e.g. a device switch, which isn't a `getEmbeddedState` input) no
// message is sent and there's nothing to await, so return at once. The plugin
// acks every processed (non-reload) update — even a content-level no-op — so a
// reference change without a content change (e.g. switching to a variation whose
// resulting mods are identical) still resolves on the ack instead of stalling on
// the page-settle timeout. Listeners are armed after the effect: the plugin's ack
// is an async postMessage round-trip, so arming synchronously here can't miss it.
// Returns whether anything the merchant (or a screenshot) can actually SEE
// moved. Callers use it to skip work that only makes sense against a changed
// preview; see the verify-suppression in the update_mods continuation.
//
// Note this is a DIFFERENT question from the reference check above, and the two
// must not be collapsed. The reference check answers "was an `ig-previewState`
// sent, so is there an ack to await" — it has to stay reference-based to mirror
// the send listener. But `getEmbeddedState` recomputes whenever `mods` changes
// identity, even when the resulting preview is byte-for-byte the same: deleting
// a mod the merchant has HIDDEN rewrites `state.mods`, so the reference moves
// while the preview cannot, because a hidden mod was already filtered out of it.
// That is precisely the case verify-suppression exists for (ADR 0011 point 5),
// so "visibly changed" is answered by the content hashes the preview itself
// keys on, not by object identity.
export const applyAndSettle = async (
  api: AppListenerApi,
  effect: () => void
): Promise<boolean> => {
  const before = getEmbeddedState(api.getState());
  effect();
  const after = getEmbeddedState(api.getState());
  if (after === before) return false;
  await Promise.race([
    api.take(actions.previewStateProcessed.match),
    waitForPageSettle(api),
  ]);
  return (
    after.hashes.mods !== before.hashes.mods ||
    after.hashes.reload !== before.hashes.reload
  );
};
